import { useState, useEffect, useCallback } from 'react';
import { apiFetch, API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Plus, Pencil, Trash2, Eye, EyeOff, X, GripVertical,
  ArrowUp, ArrowDown, Image, Gauge, Upload, Loader2
} from 'lucide-react';

const SponsorTab = () => {
  const [sponsors, setSponsors] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [spRes, stRes] = await Promise.all([
        apiFetch(`${API_BASE}/sponsors/admin/all`),
        apiFetch(`${API_BASE}/sponsors/settings`),
      ]);
      if (spRes.ok) setSponsors(await spRes.json());
      if (stRes.ok) setSettings(await stRes.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this sponsor?')) return;
    await apiFetch(`${API_BASE}/sponsors/admin/${id}`, { method: 'DELETE' });
    fetchAll();
  };

  const handleToggle = async (sponsor) => {
    await apiFetch(`${API_BASE}/sponsors/admin/${sponsor.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !sponsor.is_active }),
    });
    fetchAll();
  };

  const handleMove = async (index, direction) => {
    const newList = [...sponsors];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newList.length) return;
    [newList[index], newList[swapIdx]] = [newList[swapIdx], newList[index]];
    const items = newList.map((s, i) => ({ id: s.id, sort_order: i + 1 }));
    setSponsors(newList);
    await apiFetch(`${API_BASE}/sponsors/admin/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
  };

  const saveSettings = async (updates) => {
    setSavingSettings(true);
    const res = await apiFetch(`${API_BASE}/sponsors/admin/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (res.ok) setSettings(await res.json());
    setSavingSettings(false);
  };

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBulkUploading(true);
    const startOrder = sponsors.length + 1;
    let uploaded = 0;

    for (let i = 0; i < files.length; i++) {
      setBulkProgress(`Uploading ${i + 1} of ${files.length}...`);
      try {
        const fd = new FormData();
        fd.append('file', files[i]);
        const uploadRes = await apiFetch(`${API_BASE}/upload`, { method: 'POST', body: fd });
        if (uploadRes.ok) {
          const { url } = await uploadRes.json();
          const name = files[i].name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
          await apiFetch(`${API_BASE}/sponsors/admin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ src: url, alt: name, sort_order: startOrder + i, is_active: true }),
          });
          uploaded++;
        }
      } catch {}
    }

    setBulkProgress(`Done! ${uploaded} of ${files.length} uploaded.`);
    setBulkUploading(false);
    fetchAll();
    setTimeout(() => setBulkProgress(''), 3000);
    e.target.value = '';
  };

  return (
    <div data-testid="sponsor-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-brand-dark">Sponsor Carousel</h2>
          <p className="text-sm text-gray-500 mt-1">Manage the rotating sponsor/artist/contributor images on the event page</p>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-sponsor-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Sponsor
        </Button>
      </div>

      {/* Bulk Upload Dropzone */}
      <div className="bg-white border border-dashed border-gray-300 p-4 mb-6 text-center hover:border-brand-gold/50 transition-colors" data-testid="bulk-upload-zone">
        <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600 mb-2">Drop multiple images or click to bulk upload</p>
        <label className="inline-flex items-center gap-2 bg-brand-dark text-white px-4 py-2 text-sm font-medium cursor-pointer hover:bg-brand-dark/90 transition-colors">
          {bulkUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {bulkUploading ? 'Uploading...' : 'Choose Files'}
          <input type="file" accept="image/*" multiple onChange={handleBulkUpload} className="hidden" disabled={bulkUploading} data-testid="bulk-upload-input" />
        </label>
        {bulkProgress && <p className="text-xs text-brand-gold mt-2">{bulkProgress}</p>}
      </div>

      {/* Carousel Settings */}
      {settings && (
        <div className="bg-white border p-4 mb-6 flex flex-wrap items-center gap-6" data-testid="carousel-settings">
          <div className="flex items-center gap-3">
            <Switch checked={settings.enabled} onCheckedChange={v => saveSettings({ enabled: v })} data-testid="carousel-enabled-switch" />
            <div>
              <Label className="font-medium">Carousel Visible</Label>
              <p className="text-xs text-gray-500">Show/hide on the event page</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-gray-400" />
            <Label className="text-sm whitespace-nowrap">Rotation Speed</Label>
            <select
              value={settings.speed}
              onChange={e => saveSettings({ speed: parseFloat(e.target.value) })}
              className="h-9 border rounded-md px-2 text-sm"
              data-testid="carousel-speed-select"
            >
              <option value={40}>Fast (40s)</option>
              <option value={60}>Medium-Fast (60s)</option>
              <option value={80}>Normal (80s)</option>
              <option value={120}>Slow (120s)</option>
              <option value={160}>Very Slow (160s)</option>
            </select>
            <span className="text-xs text-gray-400">{savingSettings ? 'Saving...' : 'per full rotation'}</span>
          </div>
          <span className="text-xs text-gray-400">{sponsors.filter(s => s.is_active).length} active / {sponsors.length} total</span>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <SponsorForm
          sponsor={editing}
          nextOrder={sponsors.length + 1}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchAll(); }}
        />
      )}

      {/* Sponsor List */}
      {loading ? (
        <p className="text-gray-500 py-8 text-center">Loading sponsors...</p>
      ) : sponsors.length === 0 ? (
        <p className="text-gray-500 py-8 text-center">No sponsors yet. Add your first one!</p>
      ) : (
        <div className="space-y-2">
          {sponsors.map((s, i) => (
            <div key={s.id} className={`bg-white border p-3 flex items-center gap-3 ${!s.is_active ? 'opacity-50' : ''}`} data-testid={`sponsor-row-${s.id}`}>
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button onClick={() => handleMove(i, -1)} disabled={i === 0} className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-20"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => handleMove(i, 1)} disabled={i === sponsors.length - 1} className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-20"><ArrowDown className="w-3.5 h-3.5" /></button>
              </div>
              <div className="w-14 h-14 bg-gray-100 flex-shrink-0 overflow-hidden border">
                {s.src ? (
                  <img src={s.src} alt={s.alt} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Image className="w-5 h-5 text-gray-300" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-dark truncate">{s.alt || 'Untitled'}</p>
                <p className="text-xs text-gray-400 truncate">{s.src}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => handleToggle(s)} className="p-1.5 hover:bg-gray-100 rounded" title={s.is_active ? 'Hide' : 'Show'}>
                  {s.is_active ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                </button>
                <button onClick={() => { setEditing(s); setShowForm(true); }} className="p-1.5 hover:bg-gray-100 rounded">
                  <Pencil className="w-4 h-4 text-blue-600" />
                </button>
                <button onClick={() => handleDelete(s.id)} className="p-1.5 hover:bg-gray-100 rounded">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const SponsorForm = ({ sponsor, nextOrder, onClose, onSaved }) => {
  const [form, setForm] = useState({
    src: sponsor?.src || '',
    alt: sponsor?.alt || '',
    sort_order: sponsor?.sort_order ?? nextOrder,
    is_active: sponsor?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiFetch(`${API_BASE}/upload`, { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        setForm(f => ({ ...f, src: data.url }));
      }
    } catch {}
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = sponsor ? `${API_BASE}/sponsors/admin/${sponsor.id}` : `${API_BASE}/sponsors/admin`;
      const method = sponsor ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) onSaved();
    } catch {}
    setSaving(false);
  };

  return (
    <div className="bg-white border p-5 mb-4" data-testid="sponsor-form">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-brand-dark">{sponsor ? 'Edit Sponsor' : 'Add Sponsor'}</h3>
        <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex gap-4 items-start">
          <div className="w-20 h-20 bg-gray-100 border overflow-hidden flex-shrink-0">
            {form.src ? (
              <img src={form.src} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Image className="w-8 h-8 text-gray-300" /></div>
            )}
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <Label>Image</Label>
              <Input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} className="mt-1" />
              {uploading && <p className="text-xs text-gray-500 mt-1">Uploading...</p>}
              <div className="flex gap-1 mt-1">
                <Input placeholder="Or paste image URL" value={form.src} onChange={e => setForm(f => ({ ...f, src: e.target.value }))} className="text-sm" data-testid="sponsor-src-input" />
              </div>
            </div>
            <div>
              <Label>Description / Alt Text</Label>
              <Input value={form.alt} onChange={e => setForm(f => ({ ...f, alt: e.target.value }))} placeholder="e.g. NUR-D - Headliner" data-testid="sponsor-alt-input" />
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <Button type="submit" disabled={saving || !form.src} data-testid="sponsor-save-btn">{saving ? 'Saving...' : sponsor ? 'Update' : 'Add Sponsor'}</Button>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </div>
  );
};

export default SponsorTab;
